if (!customElements.get('private-classes-filter')) {
  // Block-scoped (not top-level): this file and teachers-filter.js are both loaded
  // as classic scripts, so top-level const/let would share one global scope across
  // files. Both once declared identically-named seed/shuffle helpers at top level,
  // and whichever loaded second failed to parse at all — silently killing its
  // customElements.define along with everything else in the file.
  const PRIVATE_CLASSES_ORDER_SEED_KEY = 'private-classes-order-seed';
  let fallbackOrderSeed = null;

  const createRandom = (seed) => () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const getSessionOrderSeed = () => {
    try {
      let seed = sessionStorage.getItem(PRIVATE_CLASSES_ORDER_SEED_KEY);
      if (!seed) {
        seed = String(Math.floor(Math.random() * 4294967296));
        sessionStorage.setItem(PRIVATE_CLASSES_ORDER_SEED_KEY, seed);
      }
      return Number(seed) >>> 0;
    } catch (e) {
      if (fallbackOrderSeed === null) {
        fallbackOrderSeed = Math.floor(Math.random() * 4294967296);
      }
      return fallbackOrderSeed;
    }
  };

  customElements.define(
    'private-classes-filter',
    class PrivateClassesFilter extends HTMLElement {
      // connectedCallback must stay a prototype method — the Custom Elements API
      // reads lifecycle callbacks off the prototype when the element is defined.
      connectedCallback() {
        this.grid = this.querySelector('[data-pc-grid]');
        this.countEl = this.querySelector('[data-pc-count]');

        if (!this.grid) return;

        if (this.initialized) {
          this.render();
          return;
        }
        this.initialized = true;

        this.heading = this.dataset.heading || '';
        // Preserve the server-rendered count (total) to restore when no filter is active.
        this.initialCountText = this.countEl ? this.countEl.textContent.trim() : '';

        if (this.dataset.order === 'random') {
          this.shuffleCards();
          this.setAttribute('data-ordered', '');
        }

        // All teachers are rendered up front; cards beyond the page size just start hidden.
        this.cards = Array.from(this.grid.querySelectorAll('[data-pc-card]'));
        this.pageSize = parseInt(this.dataset.pageSize, 10) || this.cards.length;
        this.revealCount = this.pageSize;
        // Map of group name -> Set of active values, e.g. { location: Set(['nyc']) }.
        this.activeFilters = {};

        this.checkboxes = Array.from(this.querySelectorAll('[data-pc-filter]'));
        this.applyBtn = this.querySelector('[data-pc-filter-apply]');
        this.resetBtn = this.querySelector('[data-pc-filter-reset]');

        this.loadMoreEl = this.querySelector('[data-pc-load-more]');
        this.loadMoreLink = this.querySelector('[data-pc-load-more-link]');
        this.loadMoreTotalEl = this.querySelector('[data-pc-load-more-total]');
        this.nextCountEl = this.querySelector('[data-pc-next-count]');
        this.totalCountEl = this.querySelector('[data-pc-total-count]');
        this.endMessageEl = this.querySelector('[data-pc-end-message]');
        this.endCountEl = this.querySelector('[data-pc-end-count]');

        // Match the collection filter: apply immediately when a value changes.
        this.checkboxes.forEach((cb) => {
          cb.addEventListener('change', () => this.applyFilters());
        });
        // The Apply button only closes the drawer (filtering already happened).
        if (this.applyBtn) {
          this.applyBtn.addEventListener('click', () => this.closeDrawer());
        }
        if (this.resetBtn) {
          this.resetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.resetBtn.classList.contains('disabled')) return;
            this.reset();
          });
        }
        if (this.loadMoreLink) {
          this.loadMoreLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.revealCount += this.pageSize;
            this.render();
          });
        }

        this.setupCollapsibleDetails();
        this.render();
      }

      shuffleCards = () => {
        const random = createRandom(getSessionOrderSeed());
        const cards = Array.from(this.grid.querySelectorAll('[data-pc-card]'));
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        cards.forEach((card) => this.grid.appendChild(card));
      };

      setupCollapsibleDetails = () => {
        const ANIMATING_ATTR = 'data-animating';

        this.querySelectorAll('.mobile-facets__details').forEach((details) => {
          const summary = details.querySelector('.mobile-facets__summary');
          const submenu = details.querySelector('.mobile-facets__submenu');
          if (!summary || !submenu) return;

          summary.addEventListener('click', (e) => {
            e.preventDefault();
            if (submenu.hasAttribute(ANIMATING_ATTR)) return;
            if (details.hasAttribute('open')) {
              this.animateClose(details, summary, submenu, ANIMATING_ATTR);
            } else {
              this.animateOpen(details, summary, submenu, ANIMATING_ATTR);
            }
          });
        });
      };

      animateOpen = (details, summary, submenu, animatingAttr) => {
        details.setAttribute('open', '');
        details.classList.add('is-expanded');
        summary.setAttribute('aria-expanded', 'true');
        submenu.setAttribute(animatingAttr, 'open');
        submenu.style.height = '0px';
        requestAnimationFrame(() => {
          submenu.style.height = submenu.scrollHeight + 'px';
        });
        const onEnd = (e) => {
          if (e.propertyName !== 'height' || e.target !== submenu) return;
          submenu.removeEventListener('transitionend', onEnd);
          submenu.style.height = '';
          submenu.removeAttribute(animatingAttr);
        };
        submenu.addEventListener('transitionend', onEnd);
      };

      animateClose = (details, summary, submenu, animatingAttr) => {
        submenu.setAttribute(animatingAttr, 'close');
        details.classList.remove('is-expanded');
        summary.setAttribute('aria-expanded', 'false');
        submenu.style.height = submenu.scrollHeight + 'px';
        submenu.offsetHeight; // force reflow
        submenu.style.height = '0px';
        const onEnd = (e) => {
          if (e.propertyName !== 'height' || e.target !== submenu) return;
          submenu.removeEventListener('transitionend', onEnd);
          details.removeAttribute('open');
          submenu.style.height = '';
          submenu.removeAttribute(animatingAttr);
        };
        submenu.addEventListener('transitionend', onEnd);
      };

      // A card matches when, for every group that has at least one active filter,
      // the card's values for that group intersect the active set. Groups with no
      // active filters are ignored (pass-through).
      getMatched = () => {
        const groups = Object.keys(this.activeFilters).filter((g) => this.activeFilters[g].size > 0);
        if (groups.length === 0) return this.cards;

        return this.cards.filter((card) =>
          groups.every((group) => {
            const values = (card.dataset[this.groupDatasetKey(group)] || '').split(',').filter(Boolean);
            return values.some((v) => this.activeFilters[group].has(v));
          }),
        );
      };

      groupDatasetKey = (group) => 'pc' + group.charAt(0).toUpperCase() + group.slice(1);

      applyFilters = () => {
        const activeFilters = {};
        this.checkboxes.forEach((cb) => {
          const group = cb.dataset.pcFilterGroup;
          if (!group) return;
          if (!activeFilters[group]) activeFilters[group] = new Set();
          if (cb.checked) activeFilters[group].add(cb.value);
        });
        this.activeFilters = activeFilters;
        // A new filter selection starts back at page one of the (filtered) results.
        this.revealCount = this.pageSize;
        this.render();
      };

      closeDrawer = () => {
        const drawer = this.querySelector('facet-drawer');
        if (drawer && typeof drawer.close === 'function') drawer.close();
      };

      reset = () => {
        this.checkboxes.forEach((cb) => {
          cb.checked = false;
        });
        this.activeFilters = {};
        this.revealCount = this.pageSize;
        this.render();
      };

      formatCount = (n) => (n < 10 ? '0' + n : String(n));

      render = () => {
        const matched = this.getMatched();
        const visibleCount = Math.min(this.revealCount, matched.length);
        const visibleSet = new Set(matched.slice(0, visibleCount));

        this.cards.forEach((card) => {
          card.hidden = !visibleSet.has(card);
        });

        const hasActiveFilters = Object.values(this.activeFilters).some((set) => set.size > 0);

        if (this.countEl) {
          if (!hasActiveFilters) {
            // No filter: keep the server-rendered total count.
            this.countEl.textContent = this.initialCountText;
          } else {
            const prefix = this.heading ? this.heading + ' - ' : '';
            this.countEl.textContent = prefix + this.formatCount(matched.length);
          }
        }

        this.updateLoadMore(matched.length, visibleCount);
        this.updateResetState(hasActiveFilters);
      };

      // Reveals matching cards page-size at a time, and keeps the Load More control's
      // "results" text in sync with however many cards actually match the active filter.
      updateLoadMore = (total, visibleCount) => {
        if (!this.loadMoreEl) return;

        const remaining = total - visibleCount;
        const showLoadMore = remaining > 0;

        if (this.loadMoreLink) this.loadMoreLink.hidden = !showLoadMore;
        if (this.loadMoreTotalEl) this.loadMoreTotalEl.hidden = !showLoadMore;
        if (this.endMessageEl) this.endMessageEl.hidden = showLoadMore;

        if (showLoadMore) {
          if (this.nextCountEl) this.nextCountEl.textContent = Math.min(this.pageSize, remaining);
          if (this.totalCountEl) this.totalCountEl.textContent = total;
        } else if (this.endCountEl) {
          this.endCountEl.textContent = total;
        }
      };

      // Disable RESET ALL while no filter is selected (matches the collection filter).
      updateResetState = (hasActiveFilters) => {
        if (!this.resetBtn) return;
        this.resetBtn.classList.toggle('disabled', !hasActiveFilters);
        this.resetBtn.setAttribute('aria-disabled', String(!hasActiveFilters));
      };
    },
  );
}
